import React from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL, SITE_URL } from '../../constants/brand';
import type { CityDef } from '../../data/cities';
import { CORE_CITIES, hasServiceCityPage } from '../../data/cities';
import type { ServiceCityDeepContent } from '../../data/serviceCityDeep';
import type { ServiceGuide } from '../../data/serviceGuides';
import type { ServiceDef } from '../../data/services';
import { NAV_SERVICES } from '../../data/services';
import { locationPath, ROUTES, serviceCityPath, servicePath } from '../../enum/routes';
import CtaBand from '../CtaBand';
import FaqSection from '../FaqSection';
import Seo from '../Seo';

export default function DeepServiceCityPage({
  service,
  city,
  content,
  guide,
}: {
  service: ServiceDef;
  city: CityDef;
  content: ServiceCityDeepContent;
  guide: ServiceGuide;
}) {
  const path = serviceCityPath(service.slug, city.slug);
  const otherCore = CORE_CITIES.filter((c) => c.slug !== city.slug);
  const related = NAV_SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);
  const nearbyHrefs = new Set(content.nearby.map((n) => n.href));

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: content.heroTitle,
    description: content.metaDescription,
    url: `${SITE_URL}${path}`,
    provider: {
      '@type': 'Organization',
      name: 'Epic Home Pros',
      telephone: PHONE_TEL,
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'City',
      name: city.name,
      containedInPlace: { '@type': 'State', name: 'Texas' },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <>
      <Seo
        title={content.metaTitle}
        description={content.metaDescription}
        path={path}
        jsonLd={[serviceSchema, faqSchema]}
      />

      <section className="relative isolate flex min-h-[55vh] w-full items-center overflow-hidden md:min-h-[62vh]">
        <img alt={service.heroImageAlt} className="absolute inset-0 h-full w-full object-cover" src={service.heroImage} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/70" aria-hidden="true" />
        <div className="container-page relative z-10 py-20 text-white">
          <div className="max-w-3xl">
            <div className="label-eyebrow !text-white/90">{content.eyebrow}</div>
            <h1 className="h-display mt-4 text-white drop-shadow">{content.heroTitle}</h1>
            <p className="mt-5 max-w-2xl text-base text-white/90 sm:text-lg">{content.heroLead}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link className="btn-primary" to={ROUTES.QUOTE}>
                Get a Free Quote
              </Link>
              <a
                href={`tel:${PHONE_TEL}`}
                className="btn-secondary border-white bg-white/10 text-white hover:bg-white hover:text-forest"
              >
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.introTitle}</h2>
            <div className="mt-6 space-y-4">
              {content.introParagraphs.map((p) => (
                <p key={p.slice(0, 48)} className="leading-relaxed text-ink/80">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.whyTitle}</h2>
            <ul className="mt-6 list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
              {content.whyItems.map((item) => (
                <li key={item} className="leading-relaxed">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">
              2026 {service.shortName.toLowerCase()} cost guide for {city.name}
            </h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.costLead}</p>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">{guide.cost.disclaimer}</p>
            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-xs uppercase tracking-wider text-ink/60">
                    <th className="py-3 pr-4 font-semibold">Tier</th>
                    <th className="py-3 pr-4 font-semibold">2026 planning range</th>
                    <th className="py-3 font-semibold">Typical scope</th>
                  </tr>
                </thead>
                <tbody>
                  {guide.cost.rows.map((row) => (
                    <tr key={row.tier} className="odd:bg-white even:bg-forest-tint/40">
                      <td className="py-3 pr-4 font-medium text-ink">{row.tier}</td>
                      <td className="py-3 pr-4 font-semibold text-forest">{row.cost}</td>
                      <td className="py-3 text-ink/75">{row.includes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-sm text-ink/70">
              Full statewide guide:{' '}
              <Link className="font-medium text-forest hover:underline" to={servicePath(service.slug)}>
                {service.name} cost guide
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="bg-forest-tint py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">Where the money goes</h2>
            <p className="mt-4 leading-relaxed text-ink/80">
              Component ranges below are typical Texas planning figures for 2026. They stack differently on every{' '}
              {city.name} project.
            </p>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {guide.breakdown.items.slice(0, 9).map((item) => (
              <li
                key={item.title}
                className="card-hover rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
              >
                <h3 className="font-serif text-xl text-ink">{item.title}</h3>
                <p className="mt-1 text-sm font-semibold text-forest">{item.range}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/75">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CtaBand title={content.ctaTitle} lead={content.ctaLead} />

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.servicesTitle}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.servicesIntro}</p>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {content.serviceItems.map((item) => (
              <li
                key={item.title}
                className="card-hover rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
              >
                <h3 className="font-serif text-xl text-ink">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/75">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">
              Project timelines in {city.name}
            </h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.timelineLead}</p>
            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[32rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-xs uppercase tracking-wider text-ink/60">
                    <th className="py-3 pr-4 font-semibold">Project type</th>
                    <th className="py-3 pr-4 font-semibold">Approximate duration</th>
                    <th className="py-3 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {guide.timelines.rows.map((row) => (
                    <tr key={row.tier} className="odd:bg-white even:bg-forest-tint/30">
                      <td className="py-3 pr-4 font-medium">{row.tier}</td>
                      <td className="py-3 pr-4 font-semibold text-forest">{row.duration}</td>
                      <td className="py-3 text-ink/75">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">What may be included</h2>
            <ul className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {guide.included.checklist.map((item) => (
                <li key={item} className="flex items-start gap-2 text-ink/80">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-forest" aria-hidden="true" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <ul className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {guide.included.groups.map((group) => (
                <li key={group.title} className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                  <h3 className="font-serif text-xl text-ink">{group.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/75">{group.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-forest-tint py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.materialsTitle}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.materialsIntro}</p>
            <ul className="mt-6 list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
              {content.materials.map((m) => (
                <li key={m} className="leading-relaxed">
                  {m}
                </li>
              ))}
            </ul>
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
              {guide.materials.groups.slice(0, 2).map((group) => (
                <div key={group.title} className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                  <h3 className="font-serif text-xl text-ink">{group.title}</h3>
                  <ul className="mt-3 space-y-2 text-sm text-ink/75">
                    {group.options.slice(0, 4).map((opt) => (
                      <li key={opt.name}>
                        <span className="font-medium text-ink">{opt.name}</span>
                        {opt.range ? ` - ${opt.range}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.processTitle}</h2>
            <ol className="mt-8 space-y-4">
              {content.processSteps.map((step) => (
                <li key={step.label} className="rounded-lg border border-black/5 bg-white p-5 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-forest">{step.label}</div>
                  <p className="mt-2 text-ink/80">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.considerationsTitle}</h2>
            <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {content.considerations.map((item) => (
                <li
                  key={item.title}
                  className="card-hover rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                >
                  <h3 className="font-semibold text-ink">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/75">{item.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <div className="label-eyebrow">{guide.gallery.eyebrow}</div>
            <h2 className="h-section mt-3">{guide.gallery.title}</h2>
            <p className="mt-3 text-ink/70">
              Inspiration scoped to your {city.name} project after an on-site visit.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {guide.gallery.images.map((img) => (
              <div key={img.src} className="img-zoom overflow-hidden rounded-lg">
                <img className="img-zoom-target aspect-[4/3] w-full object-cover" alt={img.alt} src={img.src} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-forest-tint py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.areasTitle}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.areasIntro}</p>
            <ul className="mt-6 list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
              {content.areas.map((a) => (
                <li key={a} className="leading-relaxed">
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <h2 className="font-serif text-2xl text-ink">Nearby areas</h2>
            <ul className="mt-5 space-y-2">
              {content.nearby.map((n) => (
                <li key={n.href}>
                  <Link className="font-medium text-forest hover:underline" to={n.href}>
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
            <ul className="mt-4 space-y-2">
              {otherCore
                .filter(
                  (c) =>
                    hasServiceCityPage(service.slug, c.slug) &&
                    !nearbyHrefs.has(serviceCityPath(service.slug, c.slug))
                )
                .map((c) => (
                  <li key={c.slug}>
                    <Link
                      className="font-medium text-forest hover:underline"
                      to={serviceCityPath(service.slug, c.slug)}
                    >
                      {service.name} in {c.name}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
          <div>
            <h2 className="font-serif text-2xl text-ink">More services in {city.name}</h2>
            <ul className="mt-5 space-y-2">
              {related.map((s) => (
                <li key={s.slug}>
                  <Link
                    className="font-medium text-forest hover:underline"
                    to={
                      hasServiceCityPage(s.slug, city.slug)
                        ? serviceCityPath(s.slug, city.slug)
                        : servicePath(s.slug)
                    }
                  >
                    {s.name} in {city.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link className="font-medium text-forest hover:underline" to={locationPath(city.slug)}>
                  All {city.name} remodeling
                </Link>
              </li>
              <li>
                <Link className="font-medium text-forest hover:underline" to={servicePath(service.slug)}>
                  Full Texas {service.shortName.toLowerCase()} guide
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <FaqSection title={`${city.name} ${service.shortName.toLowerCase()} FAQs`} items={content.faqs} />
      <CtaBand title={content.ctaTitle} lead={content.ctaLead} />
    </>
  );
}
